import { useEffect, useState } from "react";
import { Pencil, Save, Trash2, Eye, EyeOff } from "lucide-react";
import { L, cls } from "./shared";
import { CITIES, cityKey, districtsOf, suggestCity } from "../../lib/trCity";
import { http } from "../../lib/api";
import CheckList from "../CheckList";
import { BranchTypeField, AtmFields, M2Field, AreaFields, isAtm, isCorrection, enterToNext } from "./LocTypeFields";
import LocDutySummary from "./LocDutySummary";
import QuickSubUserCreate from "./QuickSubUserCreate";

let subUsersCache = null;
const subUsersListeners = new Set();
async function refreshSubUsers() {
  const r = await http.get("/atms/sub-users");
  subUsersCache = r.data;
  subUsersListeners.forEach((fn) => fn(r.data));
  return r.data;
}
// Taşeron kullanıcıları (ad, e-posta, bağlı cari) — tek sefer yüklenir; yeni kullanıcı açılınca tüm açık formlar güncellenir
function useSubUsers() {
  const [users, setUsers] = useState(subUsersCache || []);
  useEffect(() => {
    subUsersListeners.add(setUsers);
    if (!subUsersCache) refreshSubUsers().catch(() => {});
    return () => subUsersListeners.delete(setUsers);
  }, []);
  return users;
}

function Chips({ ids, options, testid, empty = "atanmadı" }) {
  return (
    <div className="min-h-[38px] flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-md border border-slate-300 bg-slate-50" data-testid={testid}>
      {ids.length ? ids.map((id) => <span key={id} className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 text-[10px] font-bold">{options.find((o) => o.value === id)?.label || id}</span>) : <span className="text-xs text-slate-400">{empty}</span>}
    </div>
  );
}

// İşi yapan taşeron = taşeron kullanıcısı (çoklu seçim): yalnız seçili "Fatura Kesen Taşeron"lara bağlı kullanıcılar listelenir; seçilen kullanıcıların carileri working_subcontractor_ids'e yazılır
function WorkingSubsField({ form, set, readOnly, caris, subOpts }) {
  const subUsers = useSubUsers();
  const userIds = form.working_user_ids || [];
  const billing = form.subcontractor_ids || [];
  const cariName = (id) => caris.find((c) => c.id === id)?.name || "";
  const toOpt = (u) => {
    const cari = u.subcontractor_id ? cariName(u.subcontractor_id) : "";
    return { value: u.id, label: cari && cari.toLocaleUpperCase("tr") !== (u.name || "").toLocaleUpperCase("tr") ? `${u.name || u.email} · ${cari}` : (u.name || u.email) };
  };
  const listed = subUsers.filter((u) => billing.includes(u.subcontractor_id));
  const userOpts = listed.map(toOpt);
  const cariIdsOf = (ids, users) => [...new Set(users.filter((u) => ids.includes(u.id) && u.subcontractor_id).map((u) => u.subcontractor_id))];
  const setUsers = (ids) => set({ working_user_ids: ids, working_subcontractor_ids: cariIdsOf(ids, subUsers) });
  const billingKey = billing.join(",");
  useEffect(() => {   // fatura kesen taşeron değişince artık listede olmayan kullanıcılar seçimden düşer
    if (readOnly || !subUsers.length) return;
    const keep = userIds.filter((id) => listed.some((u) => u.id === id));
    if (keep.length !== userIds.length) setUsers(keep);
  }, [billingKey, subUsers.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  const onCreated = async (userId) => {
    const users = await refreshSubUsers();
    const ids = [...userIds, userId];
    set({ working_user_ids: ids, working_subcontractor_ids: cariIdsOf(ids, users) });
  };
  const quickSubOpts = billing.length ? subOpts.filter((o) => billing.includes(o.value)) : subOpts;
  return (
    <L label="İşi Yapan Taşeron">
      <div className="space-y-1" data-testid="loc-field-working">
        {readOnly
          ? <Chips ids={userIds} options={subUsers.map(toOpt)} testid="loc-field-working-users-ro" empty="atanmadı" />
          : <>
            <CheckList testid="loc-field-working-users" placeholder="Taşeron kullanıcısı ara…" height="h-24" value={userIds} onChange={setUsers} options={userOpts}
              emptyText={billing.length ? "Seçili taşeronun kullanıcısı yok — aşağıdan oluşturun" : "Önce Fatura Kesen Taşeron seçin"} />
            {billing.length > 0 && <div className="text-[10px] text-slate-500" data-testid="loc-field-working-hint">Yalnız seçili fatura kesen taşeron(lar)ın kullanıcıları listelenir.</div>}
            <QuickSubUserCreate subOpts={quickSubOpts} defaultCariId={billing[0] || ""} onCreated={onCreated} />
          </>}
      </div>
    </L>
  );
}

function PassField({ label, testid, value, onChange, readOnly, canToggle, showPass, setShowPass, placeholder, toggleTestid }) {
  return (
    <L label={label}>
      <div className="relative">
        <input readOnly={readOnly} data-testid={testid} type={showPass ? "text" : "password"} autoComplete="new-password" value={value || ""} onChange={onChange} className={`${cls} font-mono pr-10`} placeholder={placeholder} />
        {canToggle && <button type="button" tabIndex={-1} onClick={() => setShowPass((v) => !v)} data-testid={toggleTestid} title={showPass ? "Gizle" : "Göster"} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900">{showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>}
      </div>
    </L>
  );
}

// SGK işyeri + e-Bildirge kimlik alanları
function SgkBlock({ form, set, readOnly, isAdmin, showPass, setShowPass }) {
  const canToggle = !readOnly || isAdmin;
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr] gap-4 border-t border-slate-100 pt-3" data-testid="loc-sgk-block">
      <L label="e-Bildirge Kullanıcı Adı">
        <div className="flex items-center gap-2">
          <input readOnly={readOnly} data-testid="loc-field-ebildirge-1" inputMode="numeric" maxLength={11} value={form.ebildirge_user1 || ""} onChange={(e) => set({ ebildirge_user1: e.target.value.replace(/\D/g, "").slice(0, 11) })} className={`${cls} font-mono`} placeholder="11111111111" />
          <span className="text-slate-500 font-bold">-</span>
          <input readOnly={readOnly} data-testid="loc-field-ebildirge-2" inputMode="numeric" maxLength={3} value={form.ebildirge_user2 || ""} onChange={(e) => set({ ebildirge_user2: e.target.value.replace(/\D/g, "").slice(0, 3) })} className={`${cls} font-mono max-w-[80px]`} placeholder="123" />
        </div>
      </L>
      <PassField label="Sistem Şifresi" testid="loc-field-sys-pass" toggleTestid="loc-pass-toggle" value={form.ebildirge_sys_pass} onChange={(e) => set({ ebildirge_sys_pass: e.target.value })} readOnly={readOnly} canToggle={canToggle} showPass={showPass} setShowPass={setShowPass} placeholder="Sistem şifresi" />
      <PassField label="İşyeri Şifresi" testid="loc-field-work-pass" toggleTestid="loc-pass-toggle-2" value={form.ebildirge_work_pass} onChange={(e) => set({ ebildirge_work_pass: e.target.value })} readOnly={readOnly} canToggle={canToggle} showPass={showPass} setShowPass={setShowPass} placeholder="İşyeri şifresi" />
    </div>
  );
}

function CodeField({ value, error, onChange }) {
  return (
    <L label="Şube Kodu">
      <input data-testid="loc-field-code" value={value} onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if ((e.key === "Tab" || e.key === "Enter") && error) e.preventDefault(); }}
        onBlur={(e) => {
          if (!error) return;
          if (e.relatedTarget && e.relatedTarget.tagName === "BUTTON") return;
          const el = e.target;
          setTimeout(() => el.focus(), 0);
        }}
        className={`${cls} ${error ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder="örn. 1234" />
      {error && <div className="text-[11px] text-red-600 font-medium mt-1" data-testid="loc-code-error">{error}</div>}
    </L>
  );
}

// Düzeltme farkları şubesi (tür DÜZELTME): yalnız tür / bölge / kod / ad — il, ilçe, taşeron, m², yetkili, SGK ve e-Bildirge alanları tutulmaz
function CorrectionLocForm({ form, set, readOnly, regionLocked, regions, codeError, onCodeChange }) {
  return (
    <div className="space-y-3" data-testid="loc-correction-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BranchTypeField form={form} set={set} readOnly={readOnly} />
        <L label="Bölge">
          <select required disabled={readOnly || regionLocked || !!form.is_correction} data-testid="loc-field-region" value={form.region_id || ""} onChange={(e) => set({ region_id: e.target.value })} className={`${cls} disabled:bg-slate-100 disabled:text-slate-600 disabled:cursor-not-allowed`}>
            <option value="" disabled>Bölge seçin…</option>
            {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </L>
        {readOnly
          ? <L label="Şube Kodu"><input readOnly data-testid="loc-field-code" value={form.branch_code} className={cls} /></L>
          : <CodeField value={form.branch_code} error={codeError} onChange={onCodeChange} />}
        <L label="Lokasyon / Şube Adı"><input readOnly={readOnly} required data-testid="loc-field-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className={cls} /></L>
      </div>
      <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-900 leading-relaxed" data-testid="loc-correction-note">
        <b>Düzeltme farkları şubesi (tür: DÜZELTME).</b> Burada yalnız hakedişlerdeki eksik / fazlalar takip edilir: il-ilçe, taşeron, m², şube yetkilisi, SGK / e-Bildirge bilgileri, hizmet fiyatları, personel ve atamalar, onay akışları bu şubede yoktur. Tutarlar <b>Veri Girişi</b>'nden ± olarak girilir ve hakedişte ayrı “Düzeltme Farkları” satırında gösterilir.
      </div>
    </div>
  );
}

// Şube (lokasyon) kartı formu: görüntüleme / düzenleme / yeni kayıt — sıra: Şube Türü · Bölge · Şube Kodu · Şube Adı · İl · İlçe · Taşeron · SGK İşyeri No
export default function LocForm({ form, setForm, readOnly, regionLocked, codeError, onCodeChange, editing, regions, appUsers, caris = [], showPass, setShowPass, isAdmin, saveLabel, onSubmit, onCancel, onEnableEdit, onRemove, children }) {
  const set = (patch) => setForm({ ...form, ...patch });
  const [areasOpen, setAreasOpen] = useState(true);   // m² dağılımı kutuları açık gelir (düğme ile kapatılabilir)
  // il değişince ilçe yeni ilin listesinde varsa korunur (BALIKKESİR→BALIKESİR: KARESİ kalır), yoksa sıfırlanır
  const setCity = (c) => set({ city: c, district: districtsOf(c).includes(form.district) ? form.district : "" });
  const subOpts = caris.filter((c) => c.is_subcontractor !== false).map((c) => ({ value: c.id, label: c.name }));
  const correction = isCorrection(form);
  return (
    <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="loc-form">
      <fieldset onKeyDown={enterToNext} title="Enter veya Tab: yan kutuya geç" className={`space-y-3 min-w-0 ${readOnly ? "[&_input]:bg-slate-50 [&_input]:text-slate-700 [&_select]:bg-slate-50" : ""}`}>
        {correction ? <CorrectionLocForm form={form} set={set} readOnly={readOnly} regionLocked={regionLocked} regions={regions} codeError={codeError} onCodeChange={onCodeChange} /> : <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <BranchTypeField form={form} set={set} readOnly={readOnly} />
          <L label="Bölge">
            <select required disabled={readOnly || regionLocked} data-testid="loc-field-region" value={form.region_id || ""} onChange={(e) => set({ region_id: e.target.value })} className={`${cls} disabled:bg-slate-100 disabled:text-slate-600 disabled:cursor-not-allowed`}>
              <option value="" disabled>Bölge seçin…</option>
              {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {regionLocked && <div className="text-[10px] text-amber-700 mt-1" data-testid="loc-region-locked-hint">Bölge otomatik seçildi — bu şube seçili bölgenin altında açılacak, değiştirilemez.</div>}
          </L>
          {readOnly
            ? <L label="Şube Kodu"><input readOnly data-testid="loc-field-code" value={form.branch_code} className={cls} placeholder="örn. 1234" /></L>
            : <CodeField value={form.branch_code} error={codeError} onChange={onCodeChange} />}
          <L label="Lokasyon / Şube Adı"><input readOnly={readOnly} required data-testid="loc-field-name" value={form.name} onChange={(e) => set({ name: e.target.value })} className={cls} placeholder="örn. 1234 Avcılar Şube" /></L>
          <L label="İl">
            <select disabled={readOnly} required data-testid="loc-field-city" value={form.city || ""} onChange={(e) => setCity(e.target.value)} className={`${cls} disabled:bg-slate-50 disabled:text-slate-700 ${form.city && !cityKey(form.city) ? "border-rose-400 bg-rose-50" : ""}`}>
              <option value="">İl seçin…</option>
              {form.city && !cityKey(form.city) && <option value={form.city}>{form.city} (listede yok — düzeltin)</option>}
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {form.city && !cityKey(form.city) && suggestCity(form.city) && !readOnly && <button type="button" onClick={() => setCity(suggestCity(form.city))} data-testid="loc-city-suggest" className="mt-1 text-[10px] font-bold text-rose-700 underline">Öneri: {suggestCity(form.city)} — tıkla düzelt</button>}
          </L>
          <L label="İlçe">
            <select disabled={readOnly || !form.city} required data-testid="loc-field-district" value={form.district || ""} onChange={(e) => set({ district: e.target.value })} className={`${cls} disabled:bg-slate-50 disabled:text-slate-700`}>
              <option value="">{form.city ? "İlçe seçin…" : "Önce il"}</option>
              {form.district && !districtsOf(form.city).includes(form.district) && <option value={form.district}>{form.district} (listede yok)</option>}
              {districtsOf(form.city).map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </L>
          <L label="Fatura Kesen Taşeron">
            {readOnly
              ? <Chips ids={form.subcontractor_ids || []} options={subOpts} testid="loc-field-subs-ro" />
              : <CheckList testid="loc-field-subs" placeholder="Taşeron ara…" height="h-24" emptyText="Taşeron carisi yok" value={form.subcontractor_ids || []} onChange={(v) => set({ subcontractor_ids: v })} options={subOpts} />}
          </L>
          <WorkingSubsField form={form} set={set} readOnly={readOnly} caris={caris} subOpts={subOpts} />
          <L label="SGK İşyeri No"><input readOnly={readOnly} data-testid="loc-field-sgk" value={form.sgk_no || ""} onChange={(e) => set({ sgk_no: e.target.value })} className={cls} placeholder="örn. 2 3456 01 01 1234567 034 12 34" /></L>
          {isAtm(form) ? <AtmFields form={form} set={set} readOnly={readOnly} /> : <M2Field form={form} set={set} readOnly={readOnly} areasOpen={areasOpen} onToggleAreas={() => setAreasOpen((v) => !v)} />}
          <L label="Şube Yetkilisi (onay maili)">
            <select disabled={readOnly} data-testid="loc-field-approver" value={form.approver_user_id || ""} onChange={(e) => set({ approver_user_id: e.target.value || null })} className={`${cls} disabled:bg-slate-50`}>
              <option value="">— seçilmedi —</option>
              {appUsers.map((u) => <option key={u.id} value={u.id}>{`${u.name} (${u.email})`}</option>)}
            </select>
          </L>
          {!isAtm(form) && areasOpen && <AreaFields form={form} set={set} readOnly={readOnly} />}
        </div>
        <SgkBlock form={form} set={set} readOnly={readOnly} isAdmin={isAdmin} showPass={showPass} setShowPass={setShowPass} />
        </>}
      </fieldset>
      {editing && editing !== "new" && !correction && <LocDutySummary locationId={editing} />}
      <div className="flex gap-2 justify-end items-center">
        <button type="button" onClick={onCancel} className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal</button>
        {readOnly ? (
          <button key="edit" type="button" onClick={(e) => { e.preventDefault(); onEnableEdit(); }} data-testid="loc-enable-edit" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"><Pencil className="w-3.5 h-3.5" /> Düzenle</button>
        ) : (
          <button key="save" type="submit" data-testid="loc-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-orange-500 text-white text-xs font-semibold hover:bg-orange-600"><Save className="w-3.5 h-3.5" /> {saveLabel}</button>
        )}
        {editing && editing !== "new" && !correction && (
          <button type="button" onClick={() => onRemove(editing)} className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-red-300 bg-white text-xs font-semibold text-red-700 hover:bg-red-50" data-testid={`loc-delete-${editing}`}><Trash2 className="w-3.5 h-3.5" /> Sil</button>
        )}
      </div>
      {children && <div className="border-t border-slate-200 pt-3" data-testid="loc-form-services">{children}</div>}
    </form>
  );
}
